import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { airports } from "@/data/airports";
import { getTerminalInfo, getAirlineEntry } from "@/data/airlineTerminals";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { PlaneTakeoff, Info, ExternalLink, MapPin, Globe } from "lucide-react";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { SchemaMarkup, generateBreadcrumbSchema, generateFAQSchema } from "@/components/SchemaMarkup";
import { LiveWaitStrip } from "@/components/airport/LiveWaitStrip";
import { getDiningInfo } from "@/data/airportDining";
import { AppStoreCTA } from "@/components/AppStoreCTA";
import NotFound from "./NotFound";

const AirlineTerminal = () => {
  const { code, airline } = useParams<{ code: string; airline: string }>();
  const upper = code?.toUpperCase() ?? "";
  const lower = code?.toLowerCase() ?? "";
  const hasDining = Boolean(getDiningInfo(code?.toUpperCase() ?? ""));
  const airport = airports.find((a) => a.code === upper);
  const info = getTerminalInfo(upper);
  const entry = getAirlineEntry(upper, airline);

  if (!airport || !info || !entry) return <NotFound />;

  const cityName = airport.city.split(",")[0];
  const url = `https://airportbuddy.app/airports/${lower}/terminals/${entry.slug}`;
  const title = `What Terminal Is ${entry.short} at ${upper}? ${entry.terminal}`;
  const description = `${entry.short} uses ${entry.terminal} at ${airport.name} (${upper})${entry.concourse ? `, ${entry.concourse}` : ""}. Plus live TSA security wait times so you know when to leave.`;

  const faqs = [
    {
      question: `What terminal is ${entry.short} at ${upper}?`,
      answer: `${entry.airline} departs from ${entry.terminal} at ${airport.name} (${upper})${entry.concourse ? `, specifically ${entry.concourse}` : ""}.${entry.note ? ` ${entry.note}` : ""} Terminal assignments can change, so always confirm on your boarding pass.`,
    },
    ...(entry.international
      ? [{
          question: `Where do ${entry.short} international flights leave from at ${upper}?`,
          answer: `${entry.short} international departures at ${upper} use ${entry.international}. Domestic flights use ${entry.terminal}.`,
        }]
      : []),
    {
      question: `Are the terminals at ${upper} connected?`,
      answer: info.connection,
    },
    {
      question: `How early should I arrive at ${upper} for a ${entry.short} flight?`,
      answer: `Arrive 2 hours before a domestic departure and 3 hours before an international one. Check the live TSA wait time for ${upper} on Airport Buddy before you leave — when the line is running long, leaving 30 minutes earlier is the difference between relaxed and running.`,
    },
    {
      question: `Which security checkpoint should I use for ${entry.short} at ${upper}?`,
      answer: `Use the checkpoint serving ${entry.terminal}. ${info.connection} Airport Buddy shows the current wait so you can judge whether it's worth walking to a different checkpoint.`,
    },
  ];

  const otherAirlines = info.airlines.filter((a) => a.slug !== entry.slug);

  return (
    <>
      <Helmet>
        <title>{title.length > 60 ? `${entry.short} Terminal at ${upper} – ${entry.terminal}` : title}</title>
        <meta name="description" content={description.slice(0, 158)} />
        <link rel="canonical" href={url} />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content={`${entry.short} at ${upper}: ${entry.terminal}`} />
        <meta property="og:description" content={description.slice(0, 158)} />
        <meta property="og:url" content={url} />
        <meta property="og:type" content="article" />
        <meta name="twitter:card" content="summary" />
      </Helmet>

      <SchemaMarkup schema={generateBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Airports", url: "/airports" },
        { name: upper, url: `/airports/${lower}` },
        { name: "Terminals", url: `/airports/${lower}/terminals` },
        { name: entry.short, url: `/airports/${lower}/terminals/${entry.slug}` },
      ])} />
      <SchemaMarkup schema={generateFAQSchema(faqs)} />

      <div className="min-h-screen bg-gradient-to-br from-background via-secondary/5 to-accent/10">
        <div className="container mx-auto px-4 pt-4 pb-12">
          <Breadcrumbs items={[
            { label: "Home", href: "/" },
            { label: "Airports", href: "/airports" },
            { label: upper, href: `/airports/${lower}` },
            { label: "Terminals", href: `/airports/${lower}/terminals` },
            { label: entry.short, href: `/airports/${lower}/terminals/${entry.slug}` },
          ]} />

          <div className="max-w-3xl mx-auto space-y-6">
            <header className="space-y-2 mt-2">
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-foreground">
                What terminal is {entry.short} at {upper}?
              </h1>
              <p className="text-base md:text-lg text-muted-foreground">
                {airport.name} · {cityName}
              </p>
            </header>

            {/* The answer, immediately */}
            <Card className="rounded-xl shadow-md border-primary/30">
              <CardContent className="py-6 space-y-3">
                <div className="flex items-start gap-3">
                  <PlaneTakeoff className="h-7 w-7 text-primary shrink-0 mt-1" aria-hidden="true" />
                  <div>
                    <p className="text-2xl sm:text-3xl font-bold text-foreground">
                      {entry.terminal}
                    </p>
                    <p className="text-base text-muted-foreground">
                      {entry.airline} at {airport.name}
                    </p>
                  </div>
                </div>

                {entry.concourse && (
                  <p className="flex items-center gap-2 text-base text-foreground">
                    <MapPin className="h-5 w-5 text-primary shrink-0" aria-hidden="true" />
                    {entry.concourse}
                  </p>
                )}
                {entry.international && (
                  <p className="flex items-center gap-2 text-base text-foreground">
                    <Globe className="h-5 w-5 text-primary shrink-0" aria-hidden="true" />
                    International departures: {entry.international}
                  </p>
                )}
                {entry.note && (
                  <p className="flex items-start gap-2 text-base text-muted-foreground">
                    <Info className="h-5 w-5 text-primary shrink-0 mt-0.5" aria-hidden="true" />
                    {entry.note}
                  </p>
                )}

                <p className="text-sm text-muted-foreground border-t pt-3">
                  Terminal assignments change. Always confirm against your boarding pass, or check{" "}
                  <a
                    href={info.officialUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline inline-flex items-center gap-1"
                  >
                    the official {upper} airport site
                    <ExternalLink className="h-3 w-3" aria-hidden="true" />
                  </a>
                  .
                </p>
              </CardContent>
            </Card>

            {/* Live wait — the reason they should stay */}
            <LiveWaitStrip airportCode={upper} />

            <AppStoreCTA
              placement="airport_hero"
              context={upper}
              headline={`Flying ${entry.short} out of ${upper}?`}
              subline={`Airport Buddy watches the ${upper} security line and tells you when to leave. Free on iPhone.`}
            />

            {/* Getting between terminals */}
            <Card className="rounded-xl shadow-md">
              <CardContent className="py-6 space-y-2">
                <h2 className="text-xl font-bold text-foreground">
                  Are {upper} terminals connected?
                </h2>
                <p className="text-base text-muted-foreground">{info.connection}</p>
              </CardContent>
            </Card>

            {/* Other airlines at this airport */}
            <Card className="rounded-xl shadow-md">
              <CardContent className="py-6 space-y-4">
                <h2 className="text-xl font-bold text-foreground">
                  Other airline terminals at {upper}
                </h2>
                <ul className="grid sm:grid-cols-2 gap-2">
                  {otherAirlines.map((a) => (
                    <li key={a.slug}>
                      <Link
                        to={`/airports/${lower}/terminals/${a.slug}`}
                        className="flex items-center justify-between gap-2 rounded-xl border px-4 py-3 min-h-[44px] text-base hover:bg-accent/50 active:scale-95 transition"
                      >
                        <span className="font-medium">{a.short}</span>
                        <Badge variant="secondary">{a.terminal}</Badge>
                      </Link>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* FAQ */}
            <Card className="rounded-xl shadow-md">
              <CardContent className="py-6 space-y-5">
                <h2 className="text-xl font-bold text-foreground">
                  {entry.short} at {upper}: common questions
                </h2>
                {faqs.map((f) => (
                  <div key={f.question} className="space-y-1">
                    <h3 className="text-base font-semibold text-foreground">{f.question}</h3>
                    <p className="text-base text-muted-foreground">{f.answer}</p>
                  </div>
                ))}
              </CardContent>
            </Card>

            <div className="flex flex-col sm:flex-row gap-3">
              <Button asChild className="min-h-[44px] rounded-xl active:scale-95 flex-1">
                <Link to={`/airports/${lower}`}>{upper} live wait times</Link>
              </Button>
              <Button asChild variant="outline" className="min-h-[44px] rounded-xl active:scale-95 flex-1">
                <Link to={`/airports/${lower}/guide`}>Full {upper} airport guide</Link>
              </Button>
              <Button asChild variant="outline" className="min-h-[44px] rounded-xl active:scale-95 flex-1">
                <Link to={`/airports/${lower}/parking`}>{upper} parking</Link>
              </Button>
              {hasDining && (
                <Button asChild variant="outline" className="min-h-[44px] rounded-xl active:scale-95 flex-1">
                  <Link to={`/airports/${lower}/dining`}>{upper} restaurants</Link>
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AirlineTerminal;
